import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import axios from 'axios';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ClearIcon from '@material-ui/icons/Clear';
import {
  Box,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from '@material-ui/core';
import {
  ChevronLeft,
  ChevronRight,
} from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';

import DigitizationFilters from './dashboard/DigitizationFilters';
import DigitizationTable from './dashboard/DigitizationTable';

// Import Google Analytics tracking utility
import { trackEvent } from '../../../../tracking/index';
import { digitizationApiKey, digitizationAWSDataApiUrl } from '../../../../utils/config';

const styles = (theme) => ({
  // Material-UI styles for the digitization dashboard components
  root: {
    padding: '1rem',
    backgroundColor: '#ffffff',
  },
  // Styling for quick summary cards section
  summaryContainer: {
    marginBottom: '1rem',
  },
  summaryCardGrid: {
    display: "flex",
    // justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    gap: "1rem",
    flexGrow: 1,
    flexBasis: "260px",
  },
  summaryCard: {
    cursor: 'pointer',
    backgroundColor: '#FFF8F8',
    border: '1px solid black',
    // height: '-webkit-fill-available',
    height: '115px',
    width: '100%',

    '&:hover': {
      backgroundColor: '#FFD7D7',
    },
  },
  summaryCardNonClickable: {
    backgroundColor: '#fff',
    border: '1px solid black',
    height: '115px',
    width: '100%',
  },
  summaryCardSelected: {
    cursor: 'pointer',
    backgroundColor: '#FFD7D7',
    border: '1px solid black',
    height: '115px',
    width: '100%',
  },
  cardContent: {
    height: 'inherit',
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  cardInnerContainer: {
    display: 'flex',
    flexDirection: 'column !important',
    flexWrap: 'nowrap !important',
    alignItems: 'center',
    justifyContent: 'center',
    // minWidth: "11rem",
    // maxWidth: "12rem",
    // flexShrink: 1,
    flexGrow: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    padding: "2px",
  },
  cardTextContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  numberLarge: {
    textAlign: 'center',
    fontWeight: 500,
        fontSize: '26px !important',

  },
  label: {
    textAlign: 'center',
    fontSize: '16px !important',
    fontWeight: 500,
  },
  accordion: {
    boxShadow: 'none',
    border: '0.1px solid #C6C6C6',
    // backgroundColor: '#FAFAFA',
    marginBottom: '1rem',
  },
  accordionSummary: {
    "&.Mui-expanded": {
      minHeight: "50px",
    },
    "& .MuiAccordionSummary-content.Mui-expanded": {
      margin: "10px 0px",
    },
  },
  // Container for filters section layout
  filtersContainer: {
    marginTop: '1rem',
    marginBottom: '1rem',
  },
});

const Digitization = ({ classes, history }) => {
  // Main data and loading states
  const [digitizationData, setDigitizationData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination state variables
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);  
  // State to manage expanded rows in table
  const [openRows, setOpenRows] = useState({});  
  // Filter state for search and criteria
  const [filters, setFilters] = useState({
    criteria: 'all',
    searchKeyword: '',
    dateFrom: '',
    dateTo: '',
  });
  
  // Search functionality states
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] = useState('');
  const searchTimeoutRef = useRef(null);
  
  // Summary dashboard data
  const [summaryData, setSummaryData] = useState({
    sourceFilesReceived: 0,
    unprocessedDocuments: 0,
    processedDocuments: 0,
    numberOfErrors: 0,
    docsWithLowerConfidence: 0,
  });
  
  // UI state for last sync time and accordion
  const [lastSyncTime, setLastSyncTime] = useState(new Date());
  const [expanded, setExpanded] = useState(false);
  const [selectedSummaryCard, setSelectedSummaryCard] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [selectedSummaryType, setSelectedSummaryType] = useState(null);

  // Track whether initial load has completed (to avoid double fetch)
  const initialLoadDone = useRef(false);

  // Combined initial load — fetches summary + first page in ONE API call
  useEffect(() => {
    const fetchInitialData = async () => {
      setSummaryLoading(true);
      setLoading(true);
      try {
        const payload = { page: 1, limit: rowsPerPage };
        const response = await axios.post(`${digitizationAWSDataApiUrl}/files/init`, payload, {
          headers: { 'x-api-key': digitizationApiKey }
        });

        if (response.data && response.data.summary) {
          setSummaryData({
            sourceFilesReceived: response.data.summary.no_of_source_files || 0,
            unprocessedDocuments: response.data.summary.no_of_processing_files || 0,
            processedDocuments: response.data.summary.no_of_processed_files || 0,
            numberOfErrors: (response.data.summary.no_of_failed_files ?? 0) + (response.data.summary.no_of_unprocessed_files ?? 0),
            docsWithLowerConfidence: response.data.summary.no_of_low_confidence_files || 0,
          });
        }

        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages || 1);
          setTotalItems(response.data.pagination.totalItems || 0);
        }
        const transformedData = response.data.files?.map((item, index) => ({
          id: index + 1,
          fileId: item.fileId,
          s3Key: item.s3Key || '',
          originalFileName: item.originalFileName || 'N/A',
          poNumber: item.poNumber || 'N/A',
          batch: item.batch || 'N/A',
          senderEmail: item.senderEmail || 'N/A',
          receivedAt: item.receivedAt,
          receivedTimestamp: item.receivedAt ? new Date(item.receivedAt).toLocaleString() : item.startTime ? new Date(item.startTime).toLocaleString() : 'N/A',
          status: item.status === 'processing' ? 'In Progress' :
                  item.status === 'processed' ? 'Success' :
                  item.status === 'queued' ? 'In Progress' :
                  item.status === 'failed' ? 'Failed' :
                  item.status === 'unprocessed' ? 'Unprocessed' :
                  item.status || 'N/A',
          queueStage: item.queueStage || 'N/A',
          cmo: item.cmo || 'N/A',
          site: item.site || 'N/A',
          totalSplits: item.totalSplits || 0,
          splits: [],
          docFormat: item.docFormat,
          startTime: item.startTime,
          endTime: item.endTime,
          timeline: item.timeline || [],
          extractedData: item.extractedData || {},
          sheetSummary: item.sheetSummary || null
        })) || [];
        setDigitizationData(transformedData);

        setSummaryLoading(false);
        setLoading(false);
        initialLoadDone.current = true;
        trackEvent('Digitization Dashboard', 'Page Load', 'dashboard');
      } catch (error) {
        console.error('Init API Error:', error);
        setSummaryLoading(false);
        setLoading(false);
        initialLoadDone.current = true;
      }
    };

    fetchInitialData();
  }, []);

  // Fetch data when pagination/filters change (skip initial load — /files/init handles it)
  useEffect(() => {
    if (!initialLoadDone.current) return;

    const shouldFetch =
      (!filters.dateFrom && !filters.dateTo) ||
      (filters.dateFrom && filters.dateTo);

    if (shouldFetch) {
      fetchData();
      trackEvent('Digitization Dashboard', 'Page Load', 'dashboard');
    }
  }, [page, rowsPerPage, filters.dateFrom, filters.dateTo, debouncedSearchKeyword]);

  // Search debouncing is disabled - search only happens on button click
  // useEffect(() => {
  //   if (searchTimeoutRef.current) {
  //     clearTimeout(searchTimeoutRef.current);
  //   }
    
  //   searchTimeoutRef.current = setTimeout(() => {
  //     setDebouncedSearchKeyword(filters.searchKeyword);
  //   }, 500);

  //   return () => {
  //     if (searchTimeoutRef.current) {
  //       clearTimeout(searchTimeoutRef.current);
  //     }
  //   };
  // }, [filters.searchKeyword]);

  // Clean up search timeout when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Fetches summary statistics from the dedicated summary API endpoint
  const fetchSummaryData = async () => {
    setSummaryLoading(true);
    try {
      const response = await axios.post(`${digitizationAWSDataApiUrl}/files/summary`, {}
        , {
        headers: {
          'x-api-key': digitizationApiKey
        }
      }
    );
      
      // Map API response to local state structure
      if (response.data && response.data.summary) {
        setSummaryData({
          sourceFilesReceived: response.data.summary.no_of_source_files || 0,
          unprocessedDocuments: response.data.summary.no_of_processing_files || 0,
          processedDocuments: response.data.summary.no_of_processed_files || 0,
          numberOfErrors: (response.data.summary.no_of_failed_files ?? 0) + (response.data.summary.no_of_unprocessed_files ?? 0),
          docsWithLowerConfidence: response.data.summary.no_of_low_confidence_files || 0,
        });
      } else {
        // Reset to zeros if no data received
        setSummaryData({
          sourceFilesReceived: 0,
          unprocessedDocuments: 0,
          processedDocuments: 0,
          numberOfErrors: 0,
          docsWithLowerConfidence: 0,
        });
      }
      setSummaryLoading(false);
    } catch (error) {
      console.error('Summary API Error:', error);
      setSummaryLoading(false);
      // Keep existing data on error
    }
  };

  // Main function to fetch digitization files with pagination and filters
  const fetchData = async (overrideSearchKeyword = null, overridePage = null) => {
    setLoading(true);
    try {
      // Build API request payload with current filters
      const payload = {
        page: overridePage !== null ? overridePage : page,
        limit: rowsPerPage
      };

      // Add date range filters if both are specified
      if (filters.dateFrom && filters.dateTo) {
        payload.from = new Date(filters.dateFrom).toISOString();
        payload.to = new Date(filters.dateTo).toISOString();
      }

      // Handle different filter criteria types
      if (filters.criteria && filters.criteria !== 'all' && filters.searchKeyword) {
        if (filters.criteria === 'processing_status') {
          // When processing status criteria is selected, use searchKeyword as status value
          payload.status = filters.searchKeyword; // 'success', 'failed', 'in-progress'
        } else if (filters.criteria === 'po_number') {
          // When PO number criteria is selected
          payload.poNumber = filters.searchKeyword;
        } else if (filters.criteria === 'batch_number') {
          // When batch number criteria is selected
          payload.batch = filters.searchKeyword;
        }
      }

      // Add search keyword filter for legacy support
      // Use overrideSearchKeyword if provided, otherwise use debouncedSearchKeyword
      const searchKeywordToUse = overrideSearchKeyword !== null ? overrideSearchKeyword : debouncedSearchKeyword;
      if (searchKeywordToUse && (!filters.criteria || filters.criteria === 'all')) {
        payload.poNumber = searchKeywordToUse;
      }

      // Add quick summary filter if one is selected
      if (selectedSummaryType) {
        payload.quickSummary = selectedSummaryType;
      }

      const response = await axios.post(`${digitizationAWSDataApiUrl}/files`, payload, {
        headers: {
          'x-api-key': digitizationApiKey
        }
      });
  
      
      // Update pagination information from response
      if (response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalItems(response.data.pagination.totalItems || 0);
      }
      
      // Use the actual page value for ID calculation
      const currentPage = overridePage !== null ? overridePage : page;
      
      // Transform raw API data into table-friendly format
      const transformedData = response.data.files?.map((item, index) => {
        return {
          id: (currentPage - 1) * rowsPerPage + index + 1,
          fileId: item.fileId,
          s3Key: item.s3Key || '',
          originalFileName: item.originalFileName || 'N/A',
          poNumber: item.poNumber || 'N/A',
          batch: item.batch || 'N/A',
          senderEmail: item.senderEmail || 'N/A',
          receivedAt: item.receivedAt,
          receivedTimestamp: item.receivedAt ? new Date(item.receivedAt).toLocaleString() : item.startTime ? new Date(item.startTime).toLocaleString() : 'N/A',
          status: item.status === 'processing' ? 'In Progress' : 
                  item.status === 'processed' ? 'Success' : 
                  item.status === 'queued' ? 'In Progress' :                   item.status === 'failed' ? 'Failed' :
                  item.status === 'unprocessed' ? 'Unprocessed' :                  item.status || 'N/A',
          queueStage: item.queueStage || 'N/A',
          cmo: item.cmo || 'N/A',
          site: item.site || 'N/A',
          totalSplits: item.totalSplits || 0,
          splits: [],
          docFormat: item.docFormat,
          startTime: item.startTime,
          endTime: item.endTime,
          timeline: item.timeline || [],
          extractedData: item.extractedData || {},
          sheetSummary: item.sheetSummary || null
        };
      }) || [];
      
      setDigitizationData(transformedData);
      setLoading(false);
    } catch (error) {
      console.error('API Error:', error);
      // Set empty array on error
      setDigitizationData([]);
      setLoading(false);
    }
  };

  // Updates filter state when user changes filter criteria or dates
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
    setOpenRows({}); // Clear expanded rows when filtering
    // Don't clear summary selection when table filters change
    // setSelectedSummaryCard(null);
    // setSelectedSummaryType(null);
    trackEvent('Digitization Dashboard', 'Filter Change', newFilters.criteria);
  };

  // Triggers API search when user clicks the search button
  const handleSearch = async () => {
    setDebouncedSearchKeyword(filters.searchKeyword);
    setPage(1);
    setOpenRows({}); // Clear expanded rows when searching
    // Don't clear summary selection when searching
    // setSelectedSummaryCard(null);
    // setSelectedSummaryType(null);
    
    // Call fetchData directly with current search keyword and page 1 to ensure API is called
    // even if the search keyword hasn't changed since last search
    await fetchData(filters.searchKeyword, 1);
    
    trackEvent('Digitization Dashboard', 'Search', filters.searchKeyword);
  };

  // Clears all filters and resets to default state
  const handleClearFilters = () => {
    setFilters({
      criteria: 'all',
      searchKeyword: '',
      dateFrom: '',
      dateTo: '',
    });
    setDebouncedSearchKeyword('');
    setPage(1);
    setSelectedSummaryCard(null);
    setSelectedSummaryType(null);
    setOpenRows({}); // Clear expanded rows when clearing filters
    trackEvent('Digitization Dashboard', 'Clear Filters', 'all');
  };

  // Clears summary card selection
  const handleClearSummary = async () => {
    setSelectedSummaryCard(null);
    setSelectedSummaryType(null);
    setPage(1);
    setOpenRows({}); // Clear expanded rows when clearing summary
    setSummaryLoading(true);
    setLoading(true);
    
    try {
      const payload = { page: 1, limit: 10 };
      const response = await axios.post(`${digitizationAWSDataApiUrl}/files/init`, payload, {
        headers: { 'x-api-key': digitizationApiKey }
      });

      if (response.data && response.data.summary) {
        setSummaryData({
          sourceFilesReceived: response.data.summary.no_of_source_files || 0,
          unprocessedDocuments: response.data.summary.no_of_processing_files || 0,
          processedDocuments: response.data.summary.no_of_processed_files || 0,
          numberOfErrors: (response.data.summary.no_of_failed_files ?? 0) + (response.data.summary.no_of_unprocessed_files ?? 0),
          docsWithLowerConfidence: response.data.summary.no_of_low_confidence_files || 0,
        });
      }

      if (response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalItems(response.data.pagination.totalItems || 0);
      }
      
      const transformedData = response.data.files?.map((item, index) => ({
        id: index + 1,
        fileId: item.fileId,
        s3Key: item.s3Key || '',
        originalFileName: item.originalFileName || 'N/A',
        poNumber: item.poNumber || 'N/A',
        batch: item.batch || 'N/A',
        senderEmail: item.senderEmail || 'N/A',
        receivedAt: item.receivedAt,
        receivedTimestamp: item.receivedAt ? new Date(item.receivedAt).toLocaleString() : item.startTime ? new Date(item.startTime).toLocaleString() : 'N/A',
        status: item.status === 'processing' ? 'In Progress' :
                item.status === 'processed' ? 'Success' :
                item.status === 'queued' ? 'In Progress' :
                item.status === 'failed' ? 'Failed' :
                item.status === 'unprocessed' ? 'Unprocessed' :
                item.status || 'N/A',
        queueStage: item.queueStage || 'N/A',
        cmo: item.cmo || 'N/A',
        site: item.site || 'N/A',
        totalSplits: item.totalSplits || 0,
        splits: [],
        docFormat: item.docFormat,
        startTime: item.startTime,
        endTime: item.endTime,
        timeline: item.timeline || [],
        extractedData: item.extractedData || {},
        sheetSummary: item.sheetSummary || null
      })) || [];
      setDigitizationData(transformedData);

      setSummaryLoading(false);
      setLoading(false);
    } catch (error) {
      console.error('Clear Summary API Error:', error);
      setSummaryLoading(false);
      setLoading(false);
    }
    
    trackEvent('Digitization Dashboard', 'Clear Summary', 'manual');
  };

  // Updates pagination when user changes rows per page
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Manually refreshes all data when user clicks refresh button
  const handleRefresh = async () => {
    const now = new Date();
    setLastSyncTime(now);
    setSummaryLoading(true);
    setLoading(true);

    try {
      const payload = { page, limit: rowsPerPage };
      if (filters.dateFrom && filters.dateTo) {
        payload.from = new Date(filters.dateFrom).toISOString();
        payload.to = new Date(filters.dateTo).toISOString();
      }
      if (selectedSummaryType) {
        payload.quickSummary = selectedSummaryType;
      }

      const response = await axios.post(`${digitizationAWSDataApiUrl}/files/init`, payload, {
        headers: { 'x-api-key': digitizationApiKey }
      });

      if (response.data && response.data.summary) {
        setSummaryData({
          sourceFilesReceived: response.data.summary.no_of_source_files || 0,
          unprocessedDocuments: response.data.summary.no_of_processing_files || 0,
          processedDocuments: response.data.summary.no_of_processed_files || 0,
          numberOfErrors: (response.data.summary.no_of_failed_files ?? 0) + (response.data.summary.no_of_unprocessed_files ?? 0),
          docsWithLowerConfidence: response.data.summary.no_of_low_confidence_files || 0,
        });
      }

      if (response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalItems(response.data.pagination.totalItems || 0);
      }
      const transformedData = response.data.files?.map((item, index) => ({
        id: (page - 1) * rowsPerPage + index + 1,
        fileId: item.fileId,
        s3Key: item.s3Key || '',
        originalFileName: item.originalFileName || 'N/A',
        poNumber: item.poNumber || 'N/A',
        batch: item.batch || 'N/A',
        senderEmail: item.senderEmail || 'N/A',
        receivedAt: item.receivedAt,
        receivedTimestamp: item.receivedAt ? new Date(item.receivedAt).toLocaleString() : item.startTime ? new Date(item.startTime).toLocaleString() : 'N/A',
        status: item.status === 'processing' ? 'In Progress' :
                item.status === 'processed' ? 'Success' :
                item.status === 'queued' ? 'In Progress' :
                item.status === 'failed' ? 'Failed' :
                item.status === 'unprocessed' ? 'Unprocessed' :
                item.status || 'N/A',
        queueStage: item.queueStage || 'N/A',
        cmo: item.cmo || 'N/A',
        site: item.site || 'N/A',
        totalSplits: item.totalSplits || 0,
        splits: [],
        docFormat: item.docFormat,
        startTime: item.startTime,
        endTime: item.endTime,
        timeline: item.timeline || [],
        extractedData: item.extractedData || {},
        sheetSummary: item.sheetSummary || null
      })) || [];
      setDigitizationData(transformedData);
    } catch (error) {
      console.error('Refresh API Error:', error);
    }

    setSummaryLoading(false);
    setLoading(false);
    trackEvent('Digitization Dashboard', 'Refresh', 'manual');
  };

  // Handles clicks on summary cards to filter data by specific categories
  const handleQuickSummaryClick = async (summaryType) => {
    setSelectedSummaryCard(summaryType);
    setPage(1); // Reset to page 1 when clicking summary cards
    setOpenRows({}); // Clear expanded rows when clicking summary card
    
    // Clear all filters when selecting a summary card
    setFilters({
      criteria: 'all',
      searchKeyword: '',
      dateFrom: '',
      dateTo: '',
    });
    
    // Clear debounced search keyword
    setDebouncedSearchKeyword('');
    
    // Set the summary type for future pagination calls
    let quickSummaryValue = null;
    if (summaryType === 'sourceFilesReceived') {
      quickSummaryValue = 'sourceFiles';
    } else if (summaryType === 'unprocessedDocuments') {
      quickSummaryValue = 'unprocessedFiles';
    }
    setSelectedSummaryType(quickSummaryValue);
    
    setLoading(true);
    try {
      // Build payload with quick summary filter parameter
      const payload = {
        page: 1, // Always start from page 1 for summary card clicks
        limit: rowsPerPage
      };

      // Set specific filter based on which summary card was clicked
      if (summaryType === 'sourceFilesReceived') {
        payload.quickSummary = 'sourceFiles';
      } else if (summaryType === 'unprocessedDocuments') {
        payload.quickSummary = 'unprocessedFiles';
      }

      // Include existing filter criteria (should be empty after clearing)
      // No date filters since they were cleared
      // No status criteria since it was reset to 'all'

      const response = await axios.post(`${digitizationAWSDataApiUrl}/files`, payload, {
        headers: {
          'x-api-key': digitizationApiKey
        }
      });
    
      
      // Update pagination data
      if (response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalItems(response.data.pagination.totalItems || 0);
      }
      
      // Transform API data to match table structure
      const transformedData = response.data.files?.map((item, index) => {
        return {
          id: (page - 1) * rowsPerPage + index + 1,
          fileId: item.fileId,
          s3Key: item.s3Key || '',
          originalFileName: item.originalFileName || 'N/A',
          poNumber: item.poNumber || 'N/A',
          batch: item.batch || 'N/A',
          senderEmail: item.senderEmail || 'N/A',
          receivedAt: item.receivedAt,
          receivedTimestamp: item.receivedAt ? new Date(item.receivedAt).toLocaleString() : item.startTime ? new Date(item.startTime).toLocaleString() : 'N/A',
          status: item.status === 'processing' ? 'In Progress' : 
                  item.status === 'processed' ? 'Success' : 
                  item.status === 'queued' ? 'In Progress' : 
                  item.status === 'failed' ? 'Failed' :
                  item.status === 'unprocessed' ? 'Unprocessed' :
                  item.status || 'N/A',
          queueStage: item.queueStage || 'N/A',
          cmo: item.cmo || 'N/A',
          site: item.site || 'N/A',
          totalSplits: item.totalSplits || 0,
          splits: [],
          docFormat: item.docFormat,
          startTime: item.startTime,
          endTime: item.endTime,
          timeline: item.timeline || [],
          extractedData: item.extractedData || {},
          sheetSummary: item.sheetSummary || null
        };
      }) || [];
      
      setDigitizationData(transformedData);
      setLoading(false);
      
      // Track the quick summary click event
      trackEvent('Digitization Dashboard', 'Quick Summary Click', summaryType);
    } catch (error) {
      console.error('Quick Summary API Error:', error);
      setDigitizationData([]);
      setLoading(false);
    }
  };



  // Use transformed data directly since API handles filtering and pagination
  const paginatedData = digitizationData;

  // Show pagination controls only when there are more items than current page size
  const showPagination = totalItems > rowsPerPage;

  // Renders the summary cards with counts and loading states
  const renderQuickSummaryCards = () => {
    // Define summary card configuration
    const summaryItems = [
      { count: summaryData.sourceFilesReceived, label: 'Source Files Received', key: 'sourceFilesReceived', color: '#000', clickable: true },
      { count: summaryData.unprocessedDocuments, label: 'In Process Documents', key: 'unprocessedDocuments', color: '#000', clickable: true },
      { count: summaryData.processedDocuments, label: 'Processed Documents', key: 'processedDocuments', color: '#28a745', clickable: false },
      { count: summaryData.numberOfErrors, label: 'Failed & Unprocessed Documents', key: 'numberOfErrors', color: '#dc3545', clickable: false },
    ];

    return (
      <Grid container className={classes.summaryCardGrid}>
        {summaryItems.map((item, index) => (
          <Grid item className={classes.summaryCardGrid} key={item.key}>
            <Card 
              className={
                item.clickable 
                  ? (selectedSummaryCard === item.key ? classes.summaryCardSelected : classes.summaryCard)
                  : classes.summaryCardNonClickable
              }
              onClick={item.clickable ? () => handleQuickSummaryClick(item.key) : undefined}
            >
              <CardContent className={classes.cardContent}>
                {summaryLoading ? (
                  <Grid container className={classes.cardInnerContainer}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                      <CircularProgress size={24} style={{ marginBottom: '8px' }} />
                      <Typography className={classes.label} style={{ fontSize: '0.7rem' }}>
                        Loading...
                      </Typography>
                    </div>
                  </Grid>
                ) : (
                <Grid container className={`quick-summary-card-wrap ${classes.cardInnerContainer}`}>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      style={{ alignItems: 'flex-end' }}
                      className={classes.cardTextContainer}
                    >
                      <Typography 
                        className={`quick-summary-count ${classes.numberLarge}`}
                        style={{ color: item.color }}
                      >
                        {item.count.toString().padStart(2, '0')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} className={classes.cardTextContainer}>
                      <Typography className={`quick-summary-label ${classes.label}`}>
                        {item.label}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <>
      {/* Header */}
      <Grid container style={{ marginBottom: '1rem' }}>
        <Grid item xs={12}>
          <Typography style={{ fontWeight: 500, fontSize: '2rem', marginTop: '0.5rem' }}>
            Digitization Dashboard
          </Typography>
        </Grid>
      </Grid>
    <div className={classes.root}>

      {/* Collapsible Quick Summary */}
      <Grid container className={classes.summaryContainer}>
        <Grid item xs={12}>
          <Accordion className={classes.accordion} expanded={expanded}>
            <AccordionSummary
              className={classes.accordionSummary}
              // expandIcon={<ExpandMoreIcon />}
              onClick={() => setExpanded(!expanded)}
            >
              <Grid container style={{ justifyContent: "space-between" }}>
                <Grid item>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      style={{ marginRight: "8px" }}
                      // onClick={() => setExpanded(!expanded)}
                      size="small"
                    >
                      {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Typography
                      variant="body2"
                      style={{ paddingBottom: "inherit", fontWeight: 600 }}
                    >
                      Quick Summary:
                    </Typography>
                  </div>
                </Grid>
                <Grid item>
                  {(selectedSummaryCard || selectedSummaryType) && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearSummary();
                      }}
                      style={{ marginRight: "8px", color: "#EB5E28", fontSize: "12px", backgroundColor: "transparent" }}
                      title="Clear Summary Filter"
                    >
                      <ClearIcon fontSize="small" /> Clear Quick Summary
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              {renderQuickSummaryCards()}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* Filters with Last Synced and PER PAGE */}
      <Grid container className={classes.filtersContainer}>
        <DigitizationFilters 
          onFilterChange={handleFilterChange} 
          filters={filters}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          lastSyncTime={lastSyncTime}
          onRefresh={handleRefresh}
          onSearch={handleSearch}
          onClearFilters={handleClearFilters}
        />
      </Grid>

      {/* Digitization Table */}
      <Grid container style={{ position: 'relative' }}>
        <DigitizationTable 
          data={paginatedData} 
          loading={loading} 
          onRefresh={handleRefresh}
          openRows={openRows}
          setOpenRows={setOpenRows}
          filters={filters}
        />
      </Grid>

      {/* Pagination Below the Table - API-based pagination */}
      {showPagination && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginTop={2}
        >
          {/* Left Arrow */}
          <IconButton
            size="small"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1 || loading}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>

          {/* Page Info in Center */}
          <Typography variant="body2" style={{ margin: "0 8px" }}>
            {`${page} out of ${totalPages} pages`}
          </Typography>

          {/* Right Arrow */}
          <IconButton
            size="small"
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page >= totalPages || loading}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      )}
    </div>
    </>
  );
};

// Redux connection currently not needed for digitization dashboard
// const mapStatetoProps = (state) => ({});
// export default connect(mapStatetoProps, {})(withStyles(styles)(withRouter(Digitization)));

export default withStyles(styles)(withRouter(Digitization));
